import React from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
  fetchWarehouseSubrentalsPage,
  type WarehouseListPage,
  type WarehouseSubrentalRow,
  type WarehouseSubrentalsParams,
} from '@/data/warehouse';
import { describeStatus, formatCount, formatMoney, pluralize } from '@/domain/warehouse';
import ErrorBanner from '../../../components/ErrorBanner';
import WindowCalendarView, {
  WindowCalendarLegend,
  type WindowCalendarItem,
  type WindowCalendarLegendItem,
} from '../../../components/calendar/WindowCalendarView';
import { readableErrorMessage } from '../../../utils/appErrors';
import { warehouseRecordHref } from './warehouseTabs';

/** At most CALENDAR_PAGE × CALENDAR_PAGES subrentals are drawn in one visible range. */
const CALENDAR_PAGE = 100;
const CALENDAR_PAGES = 3;

type SubrentalsCalendarViewProps = {
  /** The list's filters; the calendar replaces paging, grouping and the window range. */
  params: WarehouseSubrentalsParams;
};

type SubrentalCalendarItem = WindowCalendarItem<WarehouseSubrentalRow>;

const KIND_LABELS: { [kind in WarehouseSubrentalRow['kind']]: string } = {
  event: 'Event',
  shared_stock: 'Shared stock',
  vehicle: 'Vehicle',
};

const LEGEND_ITEMS: readonly WindowCalendarLegendItem[] = [
  { tone: 'neutral', label: 'Draft' },
  { tone: 'info', label: 'Waiting on the vendor' },
  { tone: 'progress', label: 'On rent' },
  { tone: 'warn', label: 'Partly received or overdue' },
  { tone: 'ok', label: 'Returned' },
  { tone: 'stopped', label: 'Cancelled' },
];

function unitsText(row: WarehouseSubrentalRow): string {
  if (row.kind === 'vehicle') return pluralize(Math.max(1, row.unitsTotal), 'vehicle');
  return `${formatCount(row.unitsIn)} of ${formatCount(row.unitsTotal)} in`;
}

function costText(row: WarehouseSubrentalRow): string | null {
  if (row.costMode === 'per_day' && row.costPerDay != null) return `${formatMoney(row.costPerDay)} / day`;
  return formatMoney(row.costAmount);
}

function toItem(row: WarehouseSubrentalRow): SubrentalCalendarItem | null {
  if (!row.windowStartDay) return null;
  const status = describeStatus('subrental', row.status);
  return {
    id: row.id,
    code: row.docNo ?? 'Subrental',
    title: row.vendor?.name ?? 'No vendor',
    subtitle: [row.lineCount > 1 ? pluralize(row.lineCount, 'line') : row.itemSummary, row.site?.name].filter(Boolean).join(' · ') || null,
    start: row.windowStartDay,
    end: row.windowEndDay ?? row.windowStartDay,
    href: warehouseRecordHref.subrental(row.id),
    tone: status.tone,
    statusLabel: status.label,
    data: row,
  };
}

function renderDayDetails(item: SubrentalCalendarItem, dateRange: string) {
  const row = item.data;
  const cost = costText(row);
  return (
    <>
      <div className="cal-day__event-meta text-xs fg-muted">
        {[dateRange, KIND_LABELS[row.kind], unitsText(row), cost].filter(Boolean).join(' · ')}
      </div>
      {item.subtitle ? <div className="cal-day__event-meta text-xs fg-muted">{item.subtitle}</div> : null}
      {row.event ? <div className="cal-day__event-meta text-xs fg-muted">{`Event ${row.event.code ?? ''}${row.event.name ? ` · ${row.event.name}` : ''}`}</div> : null}
    </>
  );
}

function openLabel(item: SubrentalCalendarItem): string {
  return `Open subrental ${item.code}`;
}

function later(left: string | null, right: string): string {
  return left && left > right ? left : right;
}

function earlier(left: string | null, right: string): string {
  return left && left < right ? left : right;
}

async function fetchCalendarRows(params: WarehouseSubrentalsParams): Promise<{ rows: WarehouseSubrentalRow[]; total: number }> {
  const rows: WarehouseSubrentalRow[] = [];
  let total = 0;
  for (let page = 0; page < CALENDAR_PAGES; page += 1) {
    const result: WarehouseListPage<WarehouseSubrentalRow> = await fetchWarehouseSubrentalsPage({
      ...params,
      offset: page * CALENDAR_PAGE,
      limit: CALENDAR_PAGE,
    });
    rows.push(...result.rows);
    total = result.total;
    if (rows.length >= total || result.rows.length < CALENDAR_PAGE) break;
  }
  return { rows, total };
}

/**
 * Subrentals › Calendar: rental windows as bars across days, coloured by status. Reads the same
 * server list as the table, so every toolbar filter applies; the visible range sets the window.
 */
export default function SubrentalsCalendarView({ params }: SubrentalsCalendarViewProps) {
  const [range, setRange] = React.useState<{ from: string; to: string } | null>(null);
  const handleRangeChange = React.useCallback(function handleRangeChange(from: string, to: string) {
    setRange((current) => (current && current.from === from && current.to === to ? current : { from, to }));
  }, []);

  const windowParams = React.useMemo<WarehouseSubrentalsParams | null>(() => (range ? {
    ...params,
    groupBy: 'off',
    sortKey: 'window',
    sortDir: 'asc',
    offset: 0,
    limit: CALENDAR_PAGE,
    windowFrom: later(params.windowFrom, range.from),
    windowTo: earlier(params.windowTo, range.to),
  } : null), [params, range]);

  const query = useQuery({
    queryKey: ['warehouse', 'subrentals-calendar', windowParams],
    queryFn: () => (windowParams ? fetchCalendarRows(windowParams) : Promise.resolve({ rows: [], total: 0 })),
    enabled: windowParams != null,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

  const rows = React.useMemo(() => query.data?.rows ?? [], [query.data]);
  const items = React.useMemo(() => rows.map(toItem).filter((item): item is SubrentalCalendarItem => item != null), [rows]);
  const total = query.data?.total ?? 0;
  const undated = rows.length - items.length;
  const countLabel = query.isLoading && !query.data
    ? 'Loading subrentals…'
    : [
      total > rows.length
        ? `(first ${formatCount(rows.length)} of ${formatCount(total)} subrentals; narrow the filters to see the rest)`
        : `(${pluralize(total, 'subrental')})`,
      undated > 0 ? `${formatCount(undated)} without dates` : null,
    ].filter(Boolean).join(' · ');

  return (
    <>
      {query.isError ? (
        <ErrorBanner
          message={readableErrorMessage(query.error, 'Subrentals could not be loaded.')}
          onRetry={() => { void query.refetch(); }}
        />
      ) : null}
      <WindowCalendarView
        items={items}
        onDateRangeChange={handleRangeChange}
        countLabel={countLabel}
        modeAriaLabel="Subrental calendar mode"
        emptyDayLabel="No subrentals on this day."
        openLabel={openLabel}
        renderDayDetails={renderDayDetails}
        legend={<WindowCalendarLegend title="Subrental status" items={LEGEND_ITEMS} />}
      />
    </>
  );
}
